import type { ReactElement } from 'react';
import { useMemo, useState } from 'react';
import type { CourseLevel } from '@cookspace-shared-contracts/core';

import { useListCoursesQuery } from '@/api/coursesApi';
import { Input } from '@/components/Input';

const LEVEL_LABEL: Record<CourseLevel, string> = {
    beginner: 'Начинающий',
    intermediate: 'Средний',
    advanced: 'Продвинутый'
};

function formatDate(iso: string | null): string {
    return iso === null ? '—' : new Date(iso).toLocaleDateString('ru-RU');
}

function formatRating(value: number | null): string {
    return value === null ? '—' : value.toFixed(1);
}

export function CoursesPage(): ReactElement {
    const [searchInput, setSearchInput] = useState('');
    const [submittedSearch, setSubmittedSearch] = useState('');

    const queryArg = useMemo(
        () => ({
            ...(submittedSearch ? { search: submittedSearch } : {}),
            limit: 50,
            offset: 0
        }),
        [submittedSearch]
    );

    const { data, isLoading, isError } = useListCoursesQuery(queryArg);

    const items = data?.items ?? [];

    return (
        <div data-testid="courses-page" className="space-y-4">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-semibold text-gray-900">Курсы</h1>
                {data && (
                    <span className="text-sm text-gray-500" data-testid="courses-total">
                        Всего: {data.total}
                    </span>
                )}
            </div>

            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    setSubmittedSearch(searchInput.trim());
                }}
                className="flex items-end gap-3 rounded border border-gray-200 bg-white p-3"
                data-testid="courses-filters"
            >
                <div className="flex-1">
                    <Input
                        label="Поиск"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        placeholder="Название курса"
                        data-testid="courses-search-input"
                    />
                </div>
                <button
                    type="submit"
                    data-testid="courses-search-submit"
                    className="rounded bg-gray-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-gray-800"
                >
                    Найти
                </button>
            </form>

            {isLoading && <div className="text-sm text-gray-500">Загрузка...</div>}
            {isError && (
                <div className="text-sm text-red-600" data-testid="courses-error">
                    Не удалось загрузить курсы
                </div>
            )}

            {!isLoading && !isError && (
                <div
                    className="overflow-hidden rounded border border-gray-200 bg-white"
                    data-testid="courses-table"
                >
                    <div className="grid grid-cols-[80px_minmax(0,2fr)_minmax(0,1fr)_120px_100px_80px_120px] gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs font-medium uppercase text-gray-600">
                        <span>ID</span>
                        <span>Название</span>
                        <span>Автор</span>
                        <span>Уровень</span>
                        <span className="text-right">Студенты</span>
                        <span className="text-right">Рейтинг</span>
                        <span>Опубликован</span>
                    </div>

                    {items.length === 0 && (
                        <div className="px-3 py-6 text-center text-sm text-gray-500">
                            Курсы не найдены
                        </div>
                    )}

                    {items.map((course) => (
                        <div
                            key={course.id}
                            className="grid grid-cols-[80px_minmax(0,2fr)_minmax(0,1fr)_120px_100px_80px_120px] items-center gap-2 border-b border-gray-100 px-3 py-2 text-sm last:border-b-0"
                            data-testid={`course-row-${course.id}`}
                        >
                            <span className="truncate font-mono text-xs text-gray-500">
                                {course.id.slice(0, 8)}
                            </span>
                            <span className="truncate text-gray-900">{course.title}</span>
                            <span className="truncate text-gray-700">
                                {course.authorDisplayName}
                            </span>
                            <span className="text-gray-700">{LEVEL_LABEL[course.level]}</span>
                            <span className="text-right text-gray-700">
                                {course.studentsCount}
                            </span>
                            <span className="text-right text-gray-700">
                                {formatRating(course.averageRating)}
                            </span>
                            <span className="text-xs text-gray-600">
                                {formatDate(course.publishedAt)}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
